/**
 * Social Login Service
 *
 * Wraps @capgo/capacitor-social-login for Apple and Google sign-in.
 * Falls back gracefully when plugin not available (web without config).
 */

import { SocialLogin } from '@capgo/capacitor-social-login';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
// iOS OAuth client (clarime-pilot). Must match the reversed-client-ID URL scheme in
// ios/App/App/Info.plist. The plugin's iOS side ignores webClientId; without
// iOSClientId Google never gets configured and GIDSignIn crashes the app on login.
const GOOGLE_IOS_CLIENT_ID = '62754893584-4j74dsftu5vdab4kbe8ina7uns34lof1.apps.googleusercontent.com';

let initialized = false;

/**
 * Initialize social login providers.
 * Call once on app startup.
 */
export async function initSocialLogin() {
  if (initialized) return;

  try {
    await SocialLogin.initialize({
      apple: {
        clientId: 'com.clarimehealth.app',
      },
      ...(GOOGLE_CLIENT_ID ? {
        google: {
          webClientId: GOOGLE_CLIENT_ID,
          iOSClientId: GOOGLE_IOS_CLIENT_ID,
          iOSServerClientId: GOOGLE_CLIENT_ID,
        },
      } : {}),
    });
    initialized = true;
    console.log('[SocialLogin] Initialized');
  } catch (err) {
    console.warn('[SocialLogin] Init failed (expected on web without config):', err.message);
  }
}

/**
 * Sign in with Apple.
 * Returns { identityToken, email, fullName } on success.
 * Throws on cancel or failure.
 */
export async function signInWithApple() {
  if (!initialized) await initSocialLogin();

  const result = await SocialLogin.login({
    provider: 'apple',
    options: {
      scopes: ['email', 'name'],
    },
  });

  const profile = result.result;
  console.log('[SocialLogin] Apple result keys:', Object.keys(profile || {}));

  // @capgo/capacitor-social-login returns the Apple identity JWT as `idToken`,
  // not `identityToken` (which is the native ASAuthorizationAppleIDCredential property name)
  const identityToken = profile.idToken || profile.identityToken;

  if (!identityToken) {
    console.error('[SocialLogin] Apple result missing identity token:', JSON.stringify(profile, null, 2));
    throw new Error('Apple sign-in did not return an identity token');
  }

  // Extract name — Apple sends givenName + familyName on first sign-in only
  const nameProfile = profile.profile || profile;
  let fullName = null;
  if (nameProfile.givenName || nameProfile.familyName) {
    fullName = [nameProfile.givenName, nameProfile.familyName].filter(Boolean).join(' ');
  }

  return {
    identityToken,
    email: nameProfile.email || profile.email || null,
    fullName,
  };
}

/**
 * Sign in with Google.
 * Returns { idToken, email, name } on success.
 * Throws on cancel or failure.
 */
export async function signInWithGoogle() {
  if (!initialized) await initSocialLogin();

  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google Sign-In not configured');
  }

  const result = await SocialLogin.login({
    provider: 'google',
    options: {
      scopes: ['email', 'profile'],
    },
  });

  const profile = result.result;

  return {
    idToken: profile.idToken,
    email: profile.email || null,
    name: profile.name || profile.givenName || null,
  };
}

/**
 * Check if Apple Sign-In is available on this platform.
 */
export function isAppleSignInAvailable() {
  // Apple Sign-In works on iOS natively and on web via Apple JS SDK
  return true;
}

/**
 * Check if Google Sign-In is available (configured).
 */
export function isGoogleSignInAvailable() {
  return !!GOOGLE_CLIENT_ID;
}

export default {
  initSocialLogin,
  signInWithApple,
  signInWithGoogle,
  isAppleSignInAvailable,
  isGoogleSignInAvailable,
};
